import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AnalysisRunning from "@/components/AnalysisRunning";
import { toast } from "sonner";
import { fetchDemo, uploadCsv, createAnalysis, getBilling, gaStart, gaStatus, gaDisconnect, gaProperties, gaData, setGaDefaultProperty } from "@/lib/api";
import { CalendarIcon, Upload, Sparkles, FileSpreadsheet, PlayCircle, ArrowRight, Link2, Unlink, Check, Lock } from "lucide-react";
import { format, subDays, differenceInCalendarDays } from "date-fns";

function fmt(d) { return d ? format(d, "MMM d, yyyy") : "Pick a date"; }
function toISO(d) { return format(d, "yyyy-MM-dd"); }

function diff(a, b) {
  if (!a || !b) return null;
  return Math.abs(differenceInCalendarDays(b, a)) + 1; // inclusive
}

function DateRangePicker({ value, onChange, testIdPrefix }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={`${testIdPrefix}-trigger`}
          className="w-full flex items-center justify-between gap-2 rounded-xl border border-black/10 bg-white px-4 py-3 hover:border-black/20"
        >
          <span className="flex items-center gap-2 text-sm">
            <CalendarIcon className="w-4 h-4 text-[#1E392A]/60" />
            {value?.from ? fmt(value.from) : "Start"} → {value?.to ? fmt(value.to) : "End"}
          </span>
          <span className="text-xs text-[#1E392A]/50">{value?.from && value?.to ? `${diff(value.from, value.to)} days` : ""}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          numberOfMonths={2}
          selected={value}
          onSelect={onChange}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

export default function Analyze() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fileRef = useRef(null);
  const [loadingDemo, setLoadingDemo] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  // Paywall awareness — surface the lock state up-front rather than failing on click.
  // `null` means "not yet known"; treat as access-granted to avoid flashing the lock
  // before we know the answer.
  const [hasAccess, setHasAccess] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const b = await getBilling();
        setHasAccess(!!b.has_access);
      } catch {
        setHasAccess(false);
      }
    })();
  }, []);
  const showLock = hasAccess === false;

  // Two periods (current + previous). Comparison is optional — empty = snapshot mode.
  const today = new Date();
  const [currentRange, setCurrentRange] = useState({ from: subDays(today, 7), to: subDays(today, 1) });
  const [previousRange, setPreviousRange] = useState({ from: undefined, to: undefined });

  const [propertyLabel, setPropertyLabel] = useState("");
  const [csvCurrent, setCsvCurrent] = useState(null);
  const [csvPrevious, setCsvPrevious] = useState(null);
  const [activeUploadSlot, setActiveUploadSlot] = useState("current");

  // GA OAuth + property selection state
  const [gaConn, setGaConn] = useState(null); // {connected: bool}
  const [gaProps, setGaProps] = useState([]);
  const [gaSelected, setGaSelected] = useState("");
  const [gaLoadingProps, setGaLoadingProps] = useState(false);
  const [gaPropsFetched, setGaPropsFetched] = useState(false);
  const [gaTab, setGaTab] = useState("ga");

  useEffect(() => {
    (async () => {
      try {
        const s = await gaStatus();
        setGaConn(s);
      } catch { setGaConn({ connected: false }); }
    })();
  }, []);

  useEffect(() => {
    const ga = searchParams.get("ga");
    if (ga === "connected") {
      toast.success("Google Analytics connected");
      setGaTab("ga");
      setGaPropsFetched(false); // re-fetch properties after connect
      gaStatus().then(setGaConn).catch(() => {});
      setSearchParams({}, { replace: true });
    } else if (ga === "error") {
      const detail = searchParams.get("detail") || "Unknown error";
      toast.error(`Could not connect Google Analytics: ${detail}`);
      setGaTab("ga");
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (gaTab !== "ga" || !gaConn?.connected || gaPropsFetched || gaLoadingProps) return;
    setGaLoadingProps(true);
    gaProperties()
      .then((r) => setGaProps(r.properties || []))
      .catch((e) => {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail || "";
        const looksLikeAuth = status === 401 || /401|invalid auth|credentials|reconnect/i.test(detail);
        if (looksLikeAuth) {
          // Mark the connection as needing reconnection so the Reconnect
          // button surfaces in the status header.
          setGaConn((c) => ({ ...(c || {}), needs_reconnect: true }));
          toast.error("Google Analytics connection expired — please click Reconnect.");
        } else {
          toast.error(detail || "Could not list GA properties");
        }
      })
      .finally(() => {
        setGaLoadingProps(false);
        setGaPropsFetched(true);
      });
  }, [gaTab, gaConn?.connected, gaPropsFetched, gaLoadingProps]);

  // Default free users to the demo tab so they're not staring at a locked one.
  // Only fire once we know the access state AND the user hasn't deep-linked into GA.
  useEffect(() => {
    if (hasAccess === false && gaTab !== "demo" && searchParams.get("ga") !== "connected") {
      setGaTab("demo");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAccess]);

  const applyPreset = (preset) => {
    // Preset helper kept for potential reuse (e.g., Dashboard one-click). UI buttons removed.
    const t = new Date();
    if (preset === "week") {
      setCurrentRange({ from: subDays(t, 7), to: subDays(t, 1) });
      setPreviousRange({ from: subDays(t, 14), to: subDays(t, 8) });
    }
  };
  void applyPreset;

  const handleUploadClick = (slot) => {
    setActiveUploadSlot(slot);
    fileRef.current?.click();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const parsed = await uploadCsv(file);
      if (activeUploadSlot === "current") setCsvCurrent(parsed);
      else setCsvPrevious(parsed);
      toast.success(`Parsed ${file.name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not parse that CSV");
    }
  };

  const runDemo = async () => {
    setLoadingDemo(true);
    try {
      const demo = await fetchDemo();
      setAnalyzing(true);
      const analysis = await createAnalysis({
        source: "demo",
        property_label: demo.property_label,
        current_period: demo.current.period,
        previous_period: demo.previous.period,
        metrics_current: demo.current.metrics,
        metrics_previous: demo.previous.metrics,
        timeseries_current: demo.current.timeseries,
        timeseries_previous: demo.previous.timeseries,
        breakdowns_current: demo.current.breakdowns,
        breakdowns_previous: demo.previous.breakdowns,
        devices_current: demo.current.devices,
        devices_previous: demo.previous.devices,
        year_ago_period: demo.year_ago?.period,
        year_ago_metrics: demo.year_ago?.metrics,
      });
      toast.success("Analysis ready");
      navigate(`/analysis/${analysis.id}`);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 402) {
        toast.error(detail || "Free demo already used. Upgrade for more.");
        navigate("/billing");
      } else {
        toast.error(detail || "Could not run demo");
      }
    } finally {
      setLoadingDemo(false);
      setAnalyzing(false);
    }
  };

  const canRunCsv = csvCurrent && currentRange?.from && currentRange?.to;

  const runCsvAnalysis = async () => {
    if (!canRunCsv) {
      toast.error("Upload the current-period CSV and pick a date range first.");
      return;
    }
    const hasComparison = csvPrevious && previousRange?.from && previousRange?.to;
    try {
      const billing = await getBilling();
      if (!billing.has_access) {
        toast.error("You need an active plan to run an analysis.");
        navigate("/billing");
        return;
      }
      setAnalyzing(true);
      const payload = {
        source: "csv",
        property_label: propertyLabel || "Your site",
        current_period: { start: toISO(currentRange.from), end: toISO(currentRange.to), label: `${fmt(currentRange.from)} → ${fmt(currentRange.to)}` },
        metrics_current: csvCurrent.metrics,
        timeseries_current: csvCurrent.timeseries,
      };
      if (hasComparison) {
        payload.previous_period = { start: toISO(previousRange.from), end: toISO(previousRange.to), label: `${fmt(previousRange.from)} → ${fmt(previousRange.to)}` };
        payload.metrics_previous = csvPrevious.metrics;
        payload.timeseries_previous = csvPrevious.timeseries;
      }
      const analysis = await createAnalysis(payload);
      toast.success("Analysis ready");
      navigate(`/analysis/${analysis.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  const connectGa = async () => {
    try {
      const { authorization_url } = await gaStart();
      window.location.href = authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Google OAuth");
    }
  };

  const disconnectGa = async () => {
    try {
      await gaDisconnect();
      setGaConn({ connected: false });
      setGaProps([]);
      setGaSelected("");
      setGaPropsFetched(false);
      toast.success("Disconnected");
    } catch (e) {
      toast.error("Could not disconnect");
    }
  };

  const canRunGa = gaConn?.connected && gaSelected && currentRange?.from && currentRange?.to;

  const runGaAnalysis = async () => {
    if (!canRunGa) {
      toast.error("Pick a property and the current date range first.");
      return;
    }
    const hasComparison = previousRange?.from && previousRange?.to;
    try {
      const billing = await getBilling();
      if (!billing.has_access) {
        toast.error("You need an active plan to run an analysis.");
        navigate("/billing");
        return;
      }
      setAnalyzing(true);
      const gaPayload = {
        property_id: gaSelected,
        current_start: toISO(currentRange.from),
        current_end: toISO(currentRange.to),
      };
      if (hasComparison) {
        gaPayload.previous_start = toISO(previousRange.from);
        gaPayload.previous_end = toISO(previousRange.to);
      }
      const data = await gaData(gaPayload);
      const prop = gaProps.find((p) => p.property_id === gaSelected);

      const createPayload = {
        source: "ga",
        property_label: prop ? `${prop.property_name} (${prop.account_name})` : "Google Analytics",
        ga_property_id: gaSelected,
        current_period: { ...data.current.period, label: `${fmt(currentRange.from)} → ${fmt(currentRange.to)}` },
        metrics_current: data.current.metrics,
        timeseries_current: data.current.timeseries,
        breakdowns_current: data.current.breakdowns,
        devices_current: data.current.devices,
      };
      if (data.previous) {
        createPayload.previous_period = { ...data.previous.period, label: `${fmt(previousRange.from)} → ${fmt(previousRange.to)}` };
        createPayload.metrics_previous = data.previous.metrics;
        createPayload.timeseries_previous = data.previous.timeseries;
        createPayload.breakdowns_previous = data.previous.breakdowns;
        createPayload.devices_previous = data.previous.devices;
      }
      if (data.year_ago) {
        createPayload.year_ago_period = data.year_ago.period;
        createPayload.year_ago_metrics = data.year_ago.metrics;
      }
      const analysis = await createAnalysis(createPayload);
      toast.success("Analysis ready");
      navigate(`/analysis/${analysis.id}`);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "";
      const looksLikeAuth = status === 401 || /401|invalid auth|credentials|reconnect/i.test(detail);
      if (looksLikeAuth) {
        setGaConn((c) => ({ ...(c || {}), needs_reconnect: true }));
        toast.error("Google Analytics connection expired — please click Reconnect.");
      } else {
        toast.error(detail || "Could not fetch GA data");
      }
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      {(analyzing || loadingDemo) && (
        <AnalysisRunning label={loadingDemo ? "Loading your demo report" : "Analyzing your data"} />
      )}
      <SiteHeader />
      <main className="max-w-5xl mx-auto px-6 md:px-8 py-10">
        <div className="mb-8">
          <div className="tag-label text-[#E27D60]">New analysis</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">Translate your analytics.</h1>
          <p className="text-[#1E392A]/60 mt-1">Pick your data and the period. We'll write the plain-English read-out.</p>
        </div>

        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleFile} data-testid="csv-file-input" />

        <Tabs value={gaTab} onValueChange={setGaTab} className="w-full">
          <TabsList className="bg-white border border-black/5 p-1 rounded-full">
            <TabsTrigger value="ga" data-testid="tab-ga" className="rounded-full data-[state=active]:bg-[#1E392A] data-[state=active]:text-white">
              <Link2 className="w-4 h-4 mr-1.5" /> Google Analytics
              {showLock && <Lock className="w-3 h-3 ml-1.5 text-[#E27D60]" />}
            </TabsTrigger>
            <TabsTrigger value="csv" data-testid="tab-csv" className="rounded-full data-[state=active]:bg-[#1E392A] data-[state=active]:text-white">
              <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Upload CSV
              {showLock && <Lock className="w-3 h-3 ml-1.5 text-[#E27D60]" />}
            </TabsTrigger>
            <TabsTrigger value="demo" data-testid="tab-demo" className="rounded-full data-[state=active]:bg-[#1E392A] data-[state=active]:text-white">
              <PlayCircle className="w-4 h-4 mr-1.5" /> Free demo
            </TabsTrigger>
          </TabsList>

          {/* Google Analytics tab */}
          <TabsContent value="ga" className="mt-6">
            <Card className="p-6 md:p-8 rounded-3xl bento-shadow border border-black/5">
              {showLock && (
                <div
                  className="mb-6 rounded-2xl bg-[#FFF1EA] border border-[#E27D60]/30 px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
                  data-testid="ga-toplevel-paywall"
                >
                  <div className="flex items-center gap-2 text-sm text-[#a8492a]">
                    <Lock className="w-4 h-4" />
                    <span><b>Heads up:</b> running a real Google Analytics report needs an active plan. Try the free demo first, or unlock now.</span>
                  </div>
                  <Button
                    onClick={() => navigate("/billing")}
                    size="sm"
                    className="rounded-full bg-[#E27D60] hover:bg-[#cf6c50] text-white whitespace-nowrap"
                    data-testid="ga-toplevel-unlock-btn"
                  >
                    Unlock $18/mo <ArrowRight className="ml-1 w-3.5 h-3.5" />
                  </Button>
                </div>
              )}

              {!gaConn?.connected ? (
                <div className="text-center py-6">
                  <div className="w-14 h-14 mx-auto rounded-2xl bg-[#F7F5F0] grid place-items-center mb-4">
                    <Link2 className="w-6 h-6 text-[#E27D60]" />
                  </div>
                  <div className="font-display text-2xl">Connect your Google Analytics</div>
                  <p className="text-[#1E392A]/65 mt-2 max-w-md mx-auto">
                    One click to pull data straight from your GA4 properties.
                  </p>

                  <div className="mt-6 mx-auto max-w-md text-left rounded-2xl bg-[#F7F5F0] border border-black/5 p-5">
                    <div className="tag-label text-[#E27D60] mb-2">What Plainly will do</div>
                    <ul className="space-y-2 text-sm text-[#1E392A]/85">
                      <li className="flex gap-2"><Check className="w-4 h-4 text-[#1E392A] mt-0.5 shrink-0" /> Read your Google Analytics 4 reports (sessions, users, pageviews, conversions, etc.) — <b>read-only</b>.</li>
                      <li className="flex gap-2"><Check className="w-4 h-4 text-[#1E392A] mt-0.5 shrink-0" /> Use Anthropic Claude to turn those numbers into a plain-English summary that only you can see.</li>
                      <li className="flex gap-2"><Check className="w-4 h-4 text-[#1E392A] mt-0.5 shrink-0" /> Store your summaries so you can re-read them any time.</li>
                    </ul>
                    <div className="tag-label text-[#1E392A]/55 mt-4 mb-2">What Plainly will NEVER do</div>
                    <ul className="space-y-2 text-sm text-[#1E392A]/85">
                      <li className="flex gap-2"><span className="text-[#a8492a] mt-0.5 shrink-0">✕</span> Write, edit, or delete anything in your Google Analytics account.</li>
                      <li className="flex gap-2"><span className="text-[#a8492a] mt-0.5 shrink-0">✕</span> Sell, share, or use your data to train AI models.</li>
                      <li className="flex gap-2"><span className="text-[#a8492a] mt-0.5 shrink-0">✕</span> Access any other part of your Google account (no Gmail, Drive, Calendar, Ads, nothing).</li>
                    </ul>
                    <p className="text-xs text-[#1E392A]/55 mt-4 leading-relaxed">
                      You can revoke Plainly's access any time at{" "}
                      <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer" className="underline">
                        myaccount.google.com/permissions
                      </a>. Google's consent screen may say "view and download your Google Analytics data" — that's Google's standard wording for read-only access; Plainly cannot write to your account.
                    </p>
                  </div>

                  <Button
                    onClick={connectGa}
                    className="mt-6 rounded-full bg-[#E27D60] hover:bg-[#cf6c50] text-white h-11 px-6"
                    data-testid="ga-connect-btn"
                  >
                    Continue to Google <ArrowRight className="ml-1.5 w-4 h-4" />
                  </Button>
                  <p className="text-xs text-[#1E392A]/45 mt-3">
                    By continuing you'll be sent to Google to grant read-only access.
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
                    <div>
                      <div className="tag-label text-[#1E392A]/55">Status</div>
                      <div className="font-display text-xl mt-0.5">
                        {gaConn?.needs_reconnect
                          ? "Reconnection needed"
                          : "Connected to Google Analytics"}
                      </div>
                      {gaConn?.needs_reconnect && (
                        <div className="text-xs text-[#a8492a] mt-1 max-w-md">
                          Your Google access expired or was revoked. Reconnect to keep weekly emails and analyses running.
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button
                        variant="outline"
                        onClick={connectGa}
                        className="rounded-full"
                        data-testid="ga-reconnect-btn"
                      >
                        <ArrowRight className="w-4 h-4 mr-1.5" />
                        {gaConn?.needs_reconnect ? "Reconnect" : "Reconnect"}
                      </Button>
                      <Button variant="outline" onClick={disconnectGa} className="rounded-full" data-testid="ga-disconnect-btn">
                        <Unlink className="w-4 h-4 mr-1.5" /> Disconnect
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label className="tag-label text-[#1E392A]/55">GA4 property</Label>
                    <Select
                      value={gaSelected}
                      onValueChange={async (val) => {
                        setGaSelected(val);
                        const prop = gaProps.find((p) => p.property_id === val);
                        if (prop) {
                          try {
                            await setGaDefaultProperty({
                              property_id: prop.property_id,
                              property_name: prop.property_name,
                              account_name: prop.account_name,
                            });
                          } catch {}
                        }
                      }}
                    >
                      <SelectTrigger className="mt-2 rounded-xl bg-white" data-testid="ga-property-select">
                        <SelectValue placeholder={gaLoadingProps ? "Loading…" : "Pick a property"} />
                      </SelectTrigger>
                      <SelectContent>
                        {gaProps.map((p) => (
                          <SelectItem key={p.property_id} value={p.property_id} data-testid={`ga-property-${p.property_id}`}>
                            {p.property_name} <span className="text-[#1E392A]/50 ml-1">· {p.account_name}</span>
                          </SelectItem>
                        ))}
                        {gaProps.length === 0 && !gaLoadingProps && (
                          <div className="px-3 py-2 text-sm text-[#1E392A]/55">No GA4 properties found on this Google account.</div>
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                    <div>
                      <Label className="tag-label text-[#1E392A]/55">Current period</Label>
                      <div className="mt-2">
                        <DateRangePicker value={currentRange} onChange={setCurrentRange} testIdPrefix="ga-current-range" />
                      </div>
                    </div>
                    <div>
                      <Label className="tag-label text-[#1E392A]/55">Compare to <span className="text-[#1E392A]/40">(optional)</span></Label>
                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex-1">
                          <DateRangePicker value={previousRange} onChange={setPreviousRange} testIdPrefix="ga-previous-range" />
                        </div>
                        {(previousRange?.from || previousRange?.to) && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setPreviousRange({ from: undefined, to: undefined })}
                            data-testid="ga-clear-previous-btn"
                            className="rounded-full"
                          >
                            Clear
                          </Button>
                        )}
                      </div>
                      <p className="mt-2 text-xs text-[#1E392A]/50">
                        Leave empty for a plain-English snapshot. Pick a range to compare and see what changed.
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 flex items-center justify-between flex-wrap gap-3" data-testid="ga-run-row">
                    {showLock ? (
                      <>
                        <div className="flex items-center gap-2 text-sm text-[#1E392A]/65" data-testid="ga-paywall-note">
                          <Lock className="w-4 h-4 text-[#E27D60]" />
                          <span>Running a real GA report needs an active plan — the demo tab is free to try first.</span>
                        </div>
                        <Button
                          onClick={() => navigate("/billing")}
                          className="rounded-full bg-[#E27D60] hover:bg-[#cf6c50] text-white h-11 px-6"
                          data-testid="ga-unlock-btn"
                        >
                          Unlock — $18/mo or $199/yr <ArrowRight className="ml-1.5 w-4 h-4" />
                        </Button>
                      </>
                    ) : (
                      <Button
                        onClick={runGaAnalysis}
                        disabled={!canRunGa || analyzing}
                        className="rounded-full bg-[#E27D60] hover:bg-[#cf6c50] text-white h-11 px-6 ml-auto"
                        data-testid="run-ga-analysis-btn"
                      >
                        {analyzing ? (<><Sparkles className="w-4 h-4 mr-1.5 animate-pulse" /> Analyzing…</>) : (<>Run analysis <ArrowRight className="ml-1.5 w-4 h-4" /></>)}
                      </Button>
                    )}
                  </div>
                </>
              )}
            </Card>
          </TabsContent>

          {/* CSV tab */}
          <TabsContent value="csv" className="mt-6">
            <Card className="p-6 md:p-8 rounded-3xl bento-shadow border border-black/5">
              <div>
                <Label className="tag-label text-[#1E392A]/55">Site name (optional)</Label>
                <Input
                  value={propertyLabel}
                  onChange={(e) => setPropertyLabel(e.target.value)}
                  placeholder="e.g. my-shop.com"
                  className="mt-2 rounded-xl bg-white max-w-md"
                  data-testid="property-label-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div>
                  <Label className="tag-label text-[#1E392A]/55">Current period</Label>
                  <div className="mt-2">
                    <DateRangePicker value={currentRange} onChange={setCurrentRange} testIdPrefix="current-range" />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleUploadClick("current")}
                    className="mt-3 rounded-xl w-full justify-start"
                    data-testid="upload-current-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {csvCurrent ? `Loaded · ${csvCurrent.timeseries?.length || 0} rows` : "Upload CSV for current period"}
                  </Button>
                  {csvCurrent && (
                    <div className="mt-3 text-xs text-[#1E392A]/60 grid grid-cols-3 gap-2">
                      <div>Sessions: <b>{csvCurrent.metrics.sessions}</b></div>
                      <div>Users: <b>{csvCurrent.metrics.users}</b></div>
                      <div>Views: <b>{csvCurrent.metrics.pageviews}</b></div>
                    </div>
                  )}
                </div>

                <div>
                  <Label className="tag-label text-[#1E392A]/55">Compare to <span className="text-[#1E392A]/40">(optional)</span></Label>
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex-1">
                      <DateRangePicker value={previousRange} onChange={setPreviousRange} testIdPrefix="previous-range" />
                    </div>
                    {(previousRange?.from || previousRange?.to || csvPrevious) && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => { setPreviousRange({ from: undefined, to: undefined }); setCsvPrevious(null); }}
                        data-testid="clear-previous-btn"
                        className="rounded-full"
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleUploadClick("previous")}
                    className="mt-3 rounded-xl w-full justify-start"
                    data-testid="upload-previous-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {csvPrevious ? `Loaded · ${csvPrevious.timeseries?.length || 0} rows` : "Upload CSV for comparison period"}
                  </Button>
                  {csvPrevious && (
                    <div className="mt-3 text-xs text-[#1E392A]/60 grid grid-cols-3 gap-2">
                      <div>Sessions: <b>{csvPrevious.metrics.sessions}</b></div>
                      <div>Users: <b>{csvPrevious.metrics.users}</b></div>
                      <div>Views: <b>{csvPrevious.metrics.pageviews}</b></div>
                    </div>
                  )}
                  <p className="mt-2 text-xs text-[#1E392A]/50">
                    Leave empty for a plain-English snapshot. Add a comparison to see what changed.
                  </p>
                </div>
              </div>

              <div className="mt-6 rounded-2xl bg-[#F7F5F0] p-4 text-sm text-[#1E392A]/70">
                <b>Tip:</b> In Google Analytics, open a report → click <i>Share this report</i> → <i>Download file</i> → choose CSV. Need column names like Date, Sessions, Active users, Views, Bounce rate.
              </div>

              <div className="mt-6 flex items-center justify-between flex-wrap gap-3" data-testid="csv-run-row">
                {showLock ? (
                  <>
                    <div className="flex items-center gap-2 text-sm text-[#1E392A]/65" data-testid="csv-paywall-note">
                      <Lock className="w-4 h-4 text-[#E27D60]" />
                      <span>Running a CSV analysis needs an active plan — try the free demo first.</span>
                    </div>
                    <Button
                      onClick={() => navigate("/billing")}
                      className="rounded-full bg-[#E27D60] hover:bg-[#cf6c50] text-white h-11 px-6"
                      data-testid="csv-unlock-btn"
                    >
                      Unlock — $18/mo or $199/yr <ArrowRight className="ml-1.5 w-4 h-4" />
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={runCsvAnalysis}
                    disabled={!canRunCsv || analyzing}
                    className="rounded-full bg-[#E27D60] hover:bg-[#cf6c50] text-white h-11 px-6 ml-auto"
                    data-testid="run-csv-analysis-btn"
                  >
                    {analyzing ? (<><Sparkles className="w-4 h-4 mr-1.5 animate-pulse" /> Analyzing…</>) : (<>Run analysis <ArrowRight className="ml-1.5 w-4 h-4" /></>)}
                  </Button>
                )}
              </div>
            </Card>
          </TabsContent>

          {/* Demo tab */}
          <TabsContent value="demo" className="mt-6">
            <Card className="p-6 md:p-8 rounded-3xl bento-shadow border border-black/5">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#F7F5F0] grid place-items-center">
                  <Sparkles className="w-5 h-5 text-[#E27D60]" />
                </div>
                <div className="flex-1">
                  <div className="font-display text-2xl">Try it with a demo dataset</div>
                  <p className="text-[#1E392A]/65 mt-1">We'll compare last 7 days vs previous 7 days using a realistic sample site. <b>Free to try once</b> — no credit needed.</p>
                </div>
              </div>
              <div className="mt-6 flex items-center justify-end gap-3">
                <span className="text-sm text-[#1E392A]/55 mr-auto">Demo is free for everyone</span>
                <Button
                  onClick={runDemo}
                  disabled={loadingDemo || analyzing}
                  className="rounded-full bg-[#1E392A] hover:bg-[#152b20] text-white h-11 px-6"
                  data-testid="run-demo-btn"
                >
                  {(loadingDemo || analyzing) ? (<><Sparkles className="w-4 h-4 mr-1.5 animate-pulse" /> Working…</>) : (<>Run demo analysis <ArrowRight className="ml-1.5 w-4 h-4" /></>)}
                </Button>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
